"use client";
import { useDispatch, useSelector } from "react-redux";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams, usePathname, useParams } from "next/navigation";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";

import { SendItemsProvider } from "@contexts/send-items-context";
import CreateShopProvider from "@contexts/create-shop-context";
import { useWorkspaceContext } from "@contexts/workspace-context";

import { useAuthContext } from "@stadium/services/auth/client";
import { setStore } from "@slices/store";
import { useAuth0V2 } from "@stadium/services/auth0";
import { useQuery } from "@stadium/services/apollo-client";
import { useWindowStorage } from "@hooks/use-window-storage";

import { useConfig } from "@stadium/hooks/lib/use-config";
import { isCustomCurrency } from "@stadium/helpers/currency";
import { cmsPage, featurePage } from "@constants/page";
import { matchesUrl } from "@stadium/helpers/dom";
import { useFlags } from "@stadium/services/launch-darkly/use-flags";
import useFetchMenu from "@hooks/menu/use-menu-fetch";

import { fetchStoreAuthBasedParams } from "@graphql/queries/stores/auth-based-params";
import { getUpdatedDummyStoreFields } from "@helpers/create-shop";
import { CurrentUserContext } from "@contexts/current-user-provider";
import { fetchStoreNumberFromSlug } from "@graphql/queries/stores/client/basic-details";

import { setMenu } from "@slices/menu";
import { setCountry } from "@slices/country";

import { getCountry } from "@selectors/country";
import {
  getDummyStoreDetails,
  getIsPricelessShop,
  getIsLockerShop,
  getStoreCurrencyCode,
  getIsBookACallEnabled,
} from "@selectors/store";
import { getStadiumCatalogNumber } from "@selectors/config";
import { getMenu } from "@selectors/menu";
import {
  getCountriesGroupedByIsoUsingCountryZoneId,
  getDefaultCurrencyObject,
} from "@stadium/redux-utils/selectors/configSelector";
import { createMenuCodeMapping, getMenuCategoryAndSubCategory, serializeMenu } from "@helpers/menu";
import { extractSlugsFromParams, getStoreSlugFromUrlWithWorkspace } from "@stadium/helpers/store";

import { CATALOG_SLUG } from "@stadium/constants/shop";
import {
  ALL_PRODUCTS,
  getStoreBasePath,
  getStoreBaseWithCountryPathKey,
  STORE_INVITE_PAGE_PATHNAME,
  STORE_ORDER_CONFIRMATION_ROUTE,
  ALL_SUBCATEGORY_CODE,
  WITHOUT_MENU_PATHNAMES,
  PATH_WITH_HEADER_MENU,
  STORE_CREATE_PAGE_PATHNAME,
  STORE_ACCESS_DENIED_PAGE,
  STORE_DEACTIVATED_PAGE,
  CATALOG_PAGE_PATHNAME,
  CATALOG_PAGE_PATHNAME_WITHOUT_WORKSPACE,
  CATALOG_ENTRIES_PAGE_PATHNAME,
  CATALOG_ENTRIES_PAGE_PATHNAME_WITHOUT_WORKSPACE,
  getCatalogBasePath,
} from "@constants/routes";
import {
  OLD_STORE_INVITE_PAGE_PATHNAME,
  OLD_STORE_ORDER_CONFIRMATION_ROUTE,
  OLD_STORE_ACCESS_DENIED_PAGE,
  OLD_STORE_DEACTIVATED_PAGE,
  OLD_STORE_CREATE_PAGE_PATHNAME,
} from "@constants/old-routes";
import { GLOBAL_COUNTRY } from "@constants/country";
import { PERSISTED_FILTER_STORAGE_KEY } from "@constants/products/listing";
import { DEMO_MODE_STORE_CURRENCY_OPTIONS, US_CURRENCY_CODE } from "@constants/store";
import { disableSearchInShopsFlag } from "@stadium/services/launch-darkly/constants";
import { DEFAULT_COUNTRY_ISO } from "@stadium/constants";
import { SWAGMAGIC_BLOG_ROUTES } from "@constants/swagmagic-blog-routes";
import { isForceTranslationsEnabled } from "@stadium/services/weglot/helpers";

const DEFAULT_STATE = {
  withMenu: true,
  withSearch: true,
  withCart: true,
  showRewardPoints: true,
  parsedCookies: {},
  withCatalogMode: false,
  pageType: "",
  source: "",
  showSendItemIcon: false,
  showHeader: true,
  showFooter: true,
  showTalkToSalesCta: false,
  breadcrumbPageType: null,
  menuIdentifier: undefined,
};

export const StoreContext = createContext({ isNewRoute: true, lang: "en" });
export const useStoreContext = () => useContext(StoreContext);

const DEFAULT_OBJ = {};

const StoreProvider = ({
  children,
  store,
  pageType: defaultPageType = DEFAULT_STATE.pageType,
  isSwagCreationShopFlow = false,
  isMarketingPage = false,
  isNewSwagmagicHost = false,
  withMenu: withMenuProp = DEFAULT_STATE.withMenu,
  menu,
}) => {
  const { pathname: rawPathname, query } = useRouter();
  const searchParams = useSearchParams();
  const params = useParams();
  const forceTranslations = isForceTranslationsEnabled(searchParams);

  const { workspaceSlug } = useWorkspaceContext();

  const showBreadCrumbs = useMemo(
    () => isNewSwagmagicHost && !matchesUrl({ url: rawPathname, pathname: SWAGMAGIC_BLOG_ROUTES }),
    [rawPathname, isNewSwagmagicHost],
  );

  const isNewShopCreationFlow = useMemo(
    () =>
      [STORE_CREATE_PAGE_PATHNAME, OLD_STORE_CREATE_PAGE_PATHNAME].some((path) =>
        rawPathname.includes(path),
      ),
    [rawPathname],
  );

  const defaultWithMenu = useMemo(
    () => withMenuProp && !WITHOUT_MENU_PATHNAMES.some((path) => rawPathname.includes(path)),
    [rawPathname, withMenuProp],
  );

  const showMenuHeader = useMemo(
    () => withMenuProp && matchesUrl({ url: rawPathname, pathname: PATH_WITH_HEADER_MENU }),
    [rawPathname, withMenuProp],
  );

  const globalStateInitialized = useRef(false);

  const { [disableSearchInShopsFlag]: isSearchDisabled } = useFlags();
  const dispatch = useDispatch();
  const countriesByIso = useConfig(getCountriesGroupedByIsoUsingCountryZoneId);
  const pathParams = useMemo(
    () => extractSlugsFromParams(forceTranslations ? query : params),
    [forceTranslations, query, params],
  );

  const { data } = useQuery(fetchStoreNumberFromSlug, {
    variables: { slug: getStoreSlugFromUrlWithWorkspace(pathParams.slug) },
    skip: !pathParams.slug || store?.number,
  });

  const storeNumberFromSlug = data?.store?.number;
  const modifiedPathParams = useMemo(
    () => ({ ...pathParams, storeNumber: storeNumberFromSlug || store?.number }),
    [pathParams, storeNumberFromSlug, store?.number],
  );

  const {
    category: categoryCode,
    countryCode,
    lang,
    slug: storeCode = store?.code || "",
    storeNumber: storeNumberFromQuery,
    isNewRoute,
    menuIdentifier: menuIdentifierFromParams,
  } = modifiedPathParams;
  const menuIdentifier = decodeURIComponent(menuIdentifierFromParams || "");

  const pathname = usePathname();

  const isKitRedemption = useMemo(
    () =>
      store?.isKitShop &&
      matchesUrl({
        url: pathname,
        pathname: [
          STORE_INVITE_PAGE_PATHNAME,
          STORE_ORDER_CONFIRMATION_ROUTE,
          OLD_STORE_INVITE_PAGE_PATHNAME,
          OLD_STORE_ORDER_CONFIRMATION_ROUTE,
        ],
      }),
    [store, pathname],
  );

  const hideMenuOnCatalogPage = useMemo(
    () =>
      !isNewSwagmagicHost &&
      matchesUrl({
        url: pathname,
        pathname: [
          CATALOG_PAGE_PATHNAME,
          CATALOG_PAGE_PATHNAME_WITHOUT_WORKSPACE,
          CATALOG_ENTRIES_PAGE_PATHNAME,
          CATALOG_ENTRIES_PAGE_PATHNAME_WITHOUT_WORKSPACE,
        ],
      }),
    [pathname, isNewSwagmagicHost],
  );

  if (!globalStateInitialized.current) {
    const countryIso = countryCode?.toUpperCase() || DEFAULT_COUNTRY_ISO;

    if (defaultWithMenu && menu) {
      const serializedMenuData = serializeMenu({
        data: menu,
        countryIso,
        store,
      });
      dispatch(setMenu(serializedMenuData));
    }

    dispatch(setCountry(countriesByIso[countryIso]));
    dispatch(setStore(store));
    globalStateInitialized.current = true;
  }

  const [pageState, setPageState] = useState(() => {
    const isOnRestrictedPage = matchesUrl({
      url: pathname,
      pathname: [
        STORE_ACCESS_DENIED_PAGE,
        STORE_DEACTIVATED_PAGE,
        OLD_STORE_ACCESS_DENIED_PAGE,
        OLD_STORE_DEACTIVATED_PAGE,
      ],
    });
    return {
      ...DEFAULT_STATE,
      pageType: defaultPageType,
      withMenu: defaultWithMenu,
      showHeader: !isMarketingPage && !isOnRestrictedPage,
      showFooter: !isMarketingPage && !isOnRestrictedPage,
      showTalkToSalesCta: getIsBookACallEnabled({ store }),
    };
  });

  const resetPageState = useCallback(() => {
    setPageState({ ...DEFAULT_STATE, showTalkToSalesCta: getIsBookACallEnabled({ store }) });
  }, []);

  const {
    withMenu,
    withSearch,
    withCart,
    showRewardPoints,
    parsedCookies,
    withCatalogMode,
    pageType,
    source,
    showSendItemIcon,
    showHeader,
    showFooter,
    showTalkToSalesCta,
    breadcrumbPageType,
  } = pageState;

  const country = useSelector(getCountry);
  const [displayCurrency, setDisplayCurrency] = useWindowStorage(
    "selectedCurrency",
    DEFAULT_OBJ,
    "sessionStorage",
  );

  const isGlobalCountry = GLOBAL_COUNTRY.iso === country?.iso;

  const { isKitShop, number: storeNumber } = store || DEFAULT_OBJ;

  const dummyStoreDetails = useSelector(getDummyStoreDetails);
  const catalogShopNumber = useConfig(getStadiumCatalogNumber);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const isLockerShop = useSelector(getIsLockerShop);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const { graphqlInterceptorAdded } = useAuthContext();
  const orderedMenuItem = useSelector(getMenu);

  const themeCollectionItems = useMemo(
    () => orderedMenuItem?.children?.filter((menuItem) => menuItem.themeCollection),
    [orderedMenuItem],
  );

  const { category, subCategory } = useMemo(() => {
    const menuCodeMapping = createMenuCodeMapping(orderedMenuItem);

    const menuItemData = menuCodeMapping[categoryCode];

    if (!menuItemData) {
      return { category: categoryCode, subCategory: ALL_SUBCATEGORY_CODE };
    }

    return getMenuCategoryAndSubCategory({
      itemData: menuItemData,
      menuCodeMapping,
    });
  }, [orderedMenuItem, categoryCode]);

  const storeDisplayCurrencyCode = displayCurrency.code;
  const { currentUserData: { walletCurrency = {} } = {}, loading: isCurrentUserDataLoading } =
    useContext(CurrentUserContext);
  const defaultCurrencyObject = useConfig(getDefaultCurrencyObject);

  const showCurrencySelector =
    !isPricelessShop &&
    storeCurrencyCode &&
    !isCustomCurrency(storeCurrencyCode) &&
    !isKitRedemption;

  const previewCatalogFromRoute = searchParams.get("previewCatalog");

  const storeBasePath = getStoreBasePath({
    countryCode,
    lang,
    storeCode,
    storeNumber,
    workspaceSlug,
    isNewShopCreationFlow,
    isSwagCreationShopFlow,
    isNewRoute,
  });

  const storeBasePathWithCountryPathKey = getStoreBaseWithCountryPathKey({
    lang,
    storeCode,
    storeNumber,
    workspaceSlug,
    isNewRoute,
  });
  const { isAuthenticated } = useAuth0V2();
  const { refetch: fetchAuthBasedStoreParams } = useQuery(fetchStoreAuthBasedParams, {
    skip: true,
  });

  const [persistedFilters, setPersistedFilters] = useWindowStorage(
    PERSISTED_FILTER_STORAGE_KEY,
    DEFAULT_OBJ,
  );

  const [previewCatalogMode, setPreviewCatalogMode] = useWindowStorage(
    storeNumber,
    { previewCatalog: false },
    "sessionStorage",
  );

  const skipMenuFetch = menu?.id
    ? menu?.countryCode?.toLowerCase() === countryCode?.toLowerCase()
    : false;

  const fallbacks = useMemo(
    () => ({
      isGlobalCountry,
      isNewShopCreationFlow,
    }),
    [isGlobalCountry, isNewShopCreationFlow],
  );

  const { loading: isMenuLoading } = useFetchMenu({
    skip: !storeNumber || !withMenu || !showHeader || skipMenuFetch,
    defaultMenuData: isPricelessShop && isNewShopCreationFlow ? undefined : menu,
    storeNumber,
    fallbacks,
    requireAuth: isKitShop,
    isSwagCreationShopFlow,
  });

  useEffect(() => {
    if (!isCurrentUserDataLoading && (isEmpty(displayCurrency) || !displayCurrency?.id)) {
      setDisplayCurrency(walletCurrency?.code ? walletCurrency : defaultCurrencyObject);
    }
  }, [defaultCurrencyObject, walletCurrency, displayCurrency, isCurrentUserDataLoading]);

  useEffect(() => {
    if (
      !isKitShop &&
      (previewCatalogFromRoute === "true" ||
        storeNumber === catalogShopNumber ||
        pageType === featurePage ||
        pageType === cmsPage ||
        storeCode === CATALOG_SLUG)
    ) {
      setPreviewCatalogMode({ previewCatalog: true });
      return () => {
        setPreviewCatalogMode({ previewCatalog: false });
      };
    }
  }, []);

  const persistedFiltersQuery = isKitShop ? persistedFilters : DEFAULT_OBJ;

  const getUpdatedStoreBasePath = useCallback(
    (options = {}) => {
      return getStoreBasePath({
        countryCode,
        lang,
        storeCode,
        storeNumber,
        workspaceSlug,
        isNewRoute,
        ...options,
      });
    },
    [countryCode, lang, storeCode, storeNumber, workspaceSlug, isNewRoute],
  );

  const getUpdatedCatalogBasePath = useCallback(
    (options = {}) => {
      return getCatalogBasePath({
        countryCode,
        lang,
        storeCode,
        workspaceSlug,
        ...options,
      });
    },
    [countryCode, lang, storeCode, workspaceSlug],
  );

  useEffect(() => {
    if (store && !isKitShop) {
      //in preview catalog mode currency should be dollar
      const configCurrency = previewCatalogMode.previewCatalog
        ? US_CURRENCY_CODE
        : dummyStoreDetails?.currency;
      const updatedStore = (() => {
        const newStore = configCurrency
          ? {
              ...store,
              config: {
                ...store.config,
                ...DEMO_MODE_STORE_CURRENCY_OPTIONS[configCurrency ?? US_CURRENCY_CODE],
              },
            }
          : store;
        return { ...newStore, ...getUpdatedDummyStoreFields(dummyStoreDetails) };
      })();
      // this is to make sure SSR works as it does today
      if (isEqual(store, updatedStore)) return;
      dispatch(setStore(updatedStore));
    }
  }, [
    dispatch,
    fetchAuthBasedStoreParams,
    isAuthenticated,
    store,
    previewCatalogMode.previewCatalog,
  ]);

  useEffect(() => {
    if (storeNumber && isAuthenticated && isLockerShop && graphqlInterceptorAdded) {
      // fetch user based store params and update them during CSR
      fetchAuthBasedStoreParams({
        number: storeNumber,
      }).then(({ data }) => dispatch(setStore(data.store)));
    }
  }, [storeNumber, isAuthenticated, isLockerShop, graphqlInterceptorAdded]);

  const onlyHasAllProductsMenu = useMemo(() => {
    const menuList = orderedMenuItem?.children || [];
    return menuList.length === 1 && menuList[0].code === ALL_PRODUCTS;
  }, [orderedMenuItem]);

  const value = useMemo(
    () => ({
      getUpdatedStoreBasePath,
      withMenu,
      withSearch: !isSearchDisabled && withSearch,
      withCart: withCart && !isPricelessShop,
      source,
      storeBasePath,
      storeBasePathWithCountryPathKey,
      parsedCookies,
      storeNumber: storeNumberFromQuery || storeNumber,
      isGlobalCountry,
      country,
      persistedFilters,
      setPersistedFilters,
      persistedFiltersQuery,
      showRewardPoints: showRewardPoints && !isKitShop,
      isKitShop,
      isPreviewCatalogMode: previewCatalogMode.previewCatalog || isMarketingPage,
      withCatalogMode,
      storeDisplayCurrencyCode: showCurrencySelector ? storeDisplayCurrencyCode : storeCurrencyCode,
      storeCurrencyCode,
      showCurrencySelector,
      displayCurrency,
      setDisplayCurrency,
      setPageState,
      resetPageState,
      isKitRedemption,
      showSendItemIcon,
      showHeader,
      showFooter,
      pageType,
      onlyHasAllProductsMenu,
      showTalkToSalesCta,
      breadcrumbPageType,
      category,
      subCategory,
      menuIdentifier,
      isMarketingPage,
      countryCode,
      lang,
      storeCode,
      workspaceSlug,
      isNewRoute,
      isNewSwagmagicHost,
      isFetchingMenu: isMenuLoading && !menu,
      getUpdatedCatalogBasePath,
      showMenuHeader,
      showBreadCrumbs,
      themeCollectionItems,
      isPricelessShop,
      hideMenuOnCatalogPage,
    }),
    [
      isSearchDisabled,
      getUpdatedStoreBasePath,
      withMenu,
      withSearch,
      withCart,
      isPricelessShop,
      source,
      storeBasePath,
      storeBasePathWithCountryPathKey,
      parsedCookies,
      storeNumber,
      isGlobalCountry,
      country,
      persistedFilters,
      setPersistedFilters,
      persistedFiltersQuery,
      showRewardPoints,
      isKitShop,
      previewCatalogMode.previewCatalog,
      withCatalogMode,
      showCurrencySelector,
      storeDisplayCurrencyCode,
      storeCurrencyCode,
      displayCurrency,
      setDisplayCurrency,
      resetPageState,
      isKitRedemption,
      showSendItemIcon,
      showHeader,
      showFooter,
      pageType,
      onlyHasAllProductsMenu,
      showTalkToSalesCta,
      breadcrumbPageType,
      category,
      subCategory,
      menuIdentifier,
      countryCode,
      lang,
      storeCode,
      workspaceSlug,
      storeNumberFromQuery,
      isMarketingPage,
      isNewRoute,
      isNewSwagmagicHost,
      isMenuLoading,
      menu,
      showMenuHeader,
      showBreadCrumbs,
      themeCollectionItems,
      hideMenuOnCatalogPage,
    ],
  );

  return (
    <StoreContext.Provider value={value}>
      <CreateShopProvider isNewShopCreationFlow={isNewShopCreationFlow}>
        <SendItemsProvider>{children}</SendItemsProvider>
      </CreateShopProvider>
    </StoreContext.Provider>
  );
};

export default StoreProvider;
